import {
  hasPassengerWithoutSite,
  IPassengerInfo,
  IRideDto,
  NO_VALUE_DISPLAY,
} from "@ryde/shared";

import {
  IRideBottomSiteDisplayItem,
  RideBottomSiteItemVariant,
} from "./ride-tabs-bottom-sites.models";

const getPassengerDisplayName = (passenger: IPassengerInfo): string => {
  const fullName =
    `${passenger.firstName ?? ""} ${passenger.lastName ?? ""}`.trim();
  const workerId = passenger.code?.trim() || NO_VALUE_DISPLAY;
  const details = `(#${workerId}, id:${passenger.id})`;

  if (fullName) {
    return `${fullName} ${details}`;
  }

  return details;
};

export const formatRideBottomSitePassengerLines = (
  passengers: IPassengerInfo[],
): string[] => passengers.map(getPassengerDisplayName);

/** Internal grouping key for passengers missing siteId — never stored in metadata. */
const NO_SITE_GROUP_KEY = "no-site" as const;
type PassengerSiteGroupKey = number | typeof NO_SITE_GROUP_KEY;

const resolvePassengerSiteKey = (
  passenger: IPassengerInfo,
): PassengerSiteGroupKey => {
  if (passenger.siteId && Number(passenger.siteId)) {
    return Number(passenger.siteId);
  }

  return NO_SITE_GROUP_KEY;
};

interface IRideBottomSitesContext {
  passengers: IPassengerInfo[];
  rideSiteId: number | undefined;
  rideSiteName: string | undefined;
}

const extractRideBottomSitesContext = (
  rideDto: Partial<IRideDto> | undefined,
): IRideBottomSitesContext => ({
  passengers: rideDto?.metadata?.passengersInfo ?? [],
  rideSiteId:
    rideDto?.siteId && Number(rideDto.siteId)
      ? Number(rideDto.siteId)
      : undefined,
  rideSiteName: rideDto?.siteName?.trim(),
});

const resolveRideBottomSiteLabel = ({
  siteId,
  rideSiteName,
  siteLabelsById,
}: {
  siteId: number;
  rideSiteName?: string;
  siteLabelsById: Map<number, string>;
}): string => rideSiteName || siteLabelsById.get(siteId) || String(siteId);

const formatRideBottomSiteGroupLabel = (
  baseLabel: string,
  passengers: IPassengerInfo[],
): string =>
  passengers.length > 0 ? `${baseLabel}(${passengers.length})` : baseLabel;

const groupPassengersBySite = (
  passengers: IPassengerInfo[],
): Map<PassengerSiteGroupKey, IPassengerInfo[]> => {
  const groupedPassengers = new Map<PassengerSiteGroupKey, IPassengerInfo[]>();

  for (const passenger of passengers) {
    const siteKey = resolvePassengerSiteKey(passenger);
    const group = groupedPassengers.get(siteKey) ?? [];
    group.push(passenger);
    groupedPassengers.set(siteKey, group);
  }

  return groupedPassengers;
};

/** (1)/(2) empty passengers — ride site chip or nothing. */
const buildRideBottomSiteItemsForNoPassengers = ({
  rideSiteId,
  rideSiteName,
  siteLabelsById,
}: {
  rideSiteId: number | undefined;
  rideSiteName: string | undefined;
  siteLabelsById: Map<number, string>;
}): IRideBottomSiteDisplayItem[] => {
  if (!rideSiteId) {
    return [];
  }

  return [
    {
      key: `ride-site-${rideSiteId}`,
      siteId: rideSiteId,
      label: resolveRideBottomSiteLabel({
        siteId: rideSiteId,
        rideSiteName,
        siteLabelsById,
      }),
      passengers: [],
      variant: RideBottomSiteItemVariant.PassengerSite,
      isClickable: true,
      showWarningIcon: false,
      showNotEqualToRideSiteNote: false,
    },
  ];
};

/** (3) one chip per passenger site group (no-site / match ride / other). */
const buildRideBottomSiteItemsFromGroups = ({
  groupedPassengers,
  rideSiteId,
  siteLabelsById,
}: {
  groupedPassengers: Map<PassengerSiteGroupKey, IPassengerInfo[]>;
  rideSiteId: number | undefined;
  siteLabelsById: Map<number, string>;
}): IRideBottomSiteDisplayItem[] => {
  const items: IRideBottomSiteDisplayItem[] = [];

  for (const [siteKey, sitePassengers] of groupedPassengers.entries()) {
    if (siteKey === NO_SITE_GROUP_KEY) {
      items.push({
        key: `no-site`,
        siteId: 0,
        label: formatRideBottomSiteGroupLabel("no_site", sitePassengers),
        passengers: sitePassengers,
        variant: RideBottomSiteItemVariant.NoSite,
        isClickable: false,
        showWarningIcon: false,
        showNotEqualToRideSiteNote: false,
      });
      continue;
    }

    const siteId = siteKey;
    const matchesRideSite = Boolean(rideSiteId && siteId === rideSiteId);

    items.push({
      key: `passenger-site-${siteId}`,
      siteId,
      label: formatRideBottomSiteGroupLabel(
        siteLabelsById.get(siteId) ?? String(siteId),
        sitePassengers,
      ),
      passengers: sitePassengers,
      variant: matchesRideSite
        ? RideBottomSiteItemVariant.MatchesRideSite
        : RideBottomSiteItemVariant.PassengerSite,
      isClickable: true,
      showWarningIcon: false,
      showNotEqualToRideSiteNote: Boolean(rideSiteId && !matchesRideSite),
    });
  }

  return items;
};

/** (4) edge: ride.siteId differs from all passenger sites. */
const appendRideSiteMismatchItemIfNeeded = ({
  items,
  passengers,
  rideSiteId,
  rideSiteName,
  groupedPassengers,
  siteLabelsById,
}: {
  items: IRideBottomSiteDisplayItem[];
  passengers: IPassengerInfo[];
  rideSiteId: number | undefined;
  rideSiteName: string | undefined;
  groupedPassengers: Map<PassengerSiteGroupKey, IPassengerInfo[]>;
  siteLabelsById: Map<number, string>;
}): void => {
  const passengerSiteIds = [...groupedPassengers.keys()].filter(
    siteKey => siteKey !== NO_SITE_GROUP_KEY,
  ) as number[];
  const shouldShowRideSiteMismatch =
    Boolean(rideSiteId) &&
    passengers.length > 0 &&
    !hasPassengerWithoutSite(passengers) &&
    !passengerSiteIds.includes(rideSiteId as number);

  if (!shouldShowRideSiteMismatch || !rideSiteId) {
    return;
  }

  items.push({
    key: `ride-site-${rideSiteId}`,
    siteId: rideSiteId,
    label: resolveRideBottomSiteLabel({
      siteId: rideSiteId,
      rideSiteName,
      siteLabelsById,
    }),
    passengers: [],
    variant: RideBottomSiteItemVariant.RideSiteMismatch,
    isClickable: true,
    showWarningIcon: true,
    showNotEqualToRideSiteNote: false,
  });
};

/** Sort: named sites first, then mismatch, then no-site last. */
const sortRideBottomSiteDisplayItems = (
  items: IRideBottomSiteDisplayItem[],
): IRideBottomSiteDisplayItem[] =>
  items.sort((left, right) => {
    if (left.variant === RideBottomSiteItemVariant.NoSite) {
      return 1;
    }
    if (right.variant === RideBottomSiteItemVariant.NoSite) {
      return -1;
    }
    if (left.variant === RideBottomSiteItemVariant.RideSiteMismatch) {
      return 1;
    }
    if (right.variant === RideBottomSiteItemVariant.RideSiteMismatch) {
      return -1;
    }

    return left.label.localeCompare(right.label);
  });

/**
 * Bottom-tab site chips (display only — not ACL).
 *
 * Data split:
 * - passenger groups from passengersInfo (incl. no-site group for display)
 * - ride.siteId shown separately only on mismatch edge case
 *
 * Cases:
 * 1. no passengers + ride.siteId → single ride-site chip
 * 2. no passengers + no ride.siteId → empty
 * 3. passengers → one chip per passenger site group (no-site / match ride / other)
 * 4. ride.siteId set, all pax have sites, none match ride → extra mismatch chip + warning
 *
 * Sort: named sites first, then mismatch, then no-site last.
 */
export const buildRideBottomSiteDisplayItems = ({
  rideDto,
  siteLabelsById,
}: {
  rideDto: Partial<IRideDto> | undefined;
  siteLabelsById: Map<number, string>;
}): IRideBottomSiteDisplayItem[] => {
  const { passengers, rideSiteId, rideSiteName } =
    extractRideBottomSitesContext(rideDto);

  if (!passengers.length) {
    return buildRideBottomSiteItemsForNoPassengers({
      rideSiteId,
      rideSiteName,
      siteLabelsById,
    });
  }

  const groupedPassengers = groupPassengersBySite(passengers);
  const items = buildRideBottomSiteItemsFromGroups({
    groupedPassengers,
    rideSiteId,
    siteLabelsById,
  });

  appendRideSiteMismatchItemIfNeeded({
    items,
    passengers,
    rideSiteId,
    rideSiteName,
    groupedPassengers,
    siteLabelsById,
  });

  return sortRideBottomSiteDisplayItems(items);
};

/**
 * Site ids to prefetch labels for bottom chips.
 * Passenger sites only — ride.siteName is used for ride-site chips.
 */
export const collectRideBottomSiteIds = (
  rideDto: Partial<IRideDto> | undefined,
): number[] => {
  const siteIds = new Set<number>();
  const passengers = rideDto?.metadata?.passengersInfo ?? [];

  for (const passenger of passengers) {
    if (passenger.siteId && Number(passenger.siteId)) {
      siteIds.add(Number(passenger.siteId));
    }
  }

  return [...siteIds];
};
